// Apolo IDE — useActiveDispatches.
//
// Surfaces LIVE multi-agent workflows (and the most recent completed one,
// for the idle state) to two places:
//   - the Command Center macro lane (every session)
//   - the per-session micro indicator in the Code chat bar (one session)
//
// RENDERER-ONLY: it composes the dispatch endpoints that already exist
// (used by HistoryTab + useOrchestrator), so it needs no backend change and
// works against the already-deployed API right after an .app rebuild:
//   GET /api/agents/dispatch?status=running,pending           (active list)
//   GET /api/agents/dispatch?status=completed,failed,cancelled (idle fallback)
//   GET /api/agents/dispatch/:id  → { dispatch, runs, progress } (agents)
//
// Shape returned:
//   { workflows: [{ dispatch, progress, agents }], recent, loading, error, refresh }
// `agents` are slim rows (see agentFromRun). `recent` is non-null only when
// there are no live workflows and includeRecent is set.

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api.js';
import { agentFromRun } from '../components/code/agents/agentFormat.js';

const DEFAULT_POLL_MS = 2500;
const MAX_DETAIL = 12;

async function defaultFetcher(path) {
  return apiFetch(path);
}

function unwrap(res) {
  if (!res || !res.ok) return null;
  return res.data || res;
}

async function fetchList(fetcher, statusCsv, limit) {
  const params = new URLSearchParams();
  params.set('status', statusCsv);
  if (limit) params.set('limit', String(limit));
  const data = unwrap(await fetcher(`/api/agents/dispatch?${params.toString()}`));
  return data && Array.isArray(data.dispatches) ? data.dispatches : [];
}

async function fetchDetail(fetcher, id) {
  const data = unwrap(await fetcher(`/api/agents/dispatch/${encodeURIComponent(id)}`));
  if (!data) return null;
  return {
    dispatch: data.dispatch || null,
    progress: data.progress || null,
    agents: Array.isArray(data.runs) ? data.runs.map(agentFromRun) : [],
  };
}

// When detail isn't loaded, derive a coarse progress from the row so cards
// can still show "N agentes" + an indeterminate-ish bar.
function coarseProgress(row) {
  const total = Number(row.subagent_count || 0);
  return { total, pending: total, running: 0, completed: 0, failed: 0, cancelled: 0 };
}

function enrich(row, detail) {
  return {
    dispatch: (detail && detail.dispatch) || row,
    progress: (detail && detail.progress) || coarseProgress(row),
    agents: (detail && detail.agents) || [],
  };
}

// Pure, testable core. Returns { workflows, recent }.
export async function loadActiveDispatches(fetcher, options = {}) {
  const {
    sessionId = null,
    withDetail = true,
    includeRecent = true,
    max = MAX_DETAIL,
  } = options;

  let active = await fetchList(fetcher, 'running,pending', 50);
  if (sessionId) active = active.filter((d) => d.session_id === sessionId);
  active = active.slice(0, max);

  let workflows;
  if (withDetail && active.length > 0) {
    const details = await Promise.all(active.map((d) => fetchDetail(fetcher, d.id)));
    workflows = active.map((d, i) => enrich(d, details[i]));
  } else {
    workflows = active.map((d) => enrich(d, null));
  }

  let recent = null;
  if (includeRecent && workflows.length === 0) {
    let terminal = await fetchList(fetcher, 'completed,failed,cancelled', sessionId ? 20 : 1);
    if (sessionId) terminal = terminal.filter((d) => d.session_id === sessionId);
    const top = terminal[0];
    if (top) {
      const detail = withDetail ? await fetchDetail(fetcher, top.id) : null;
      recent = enrich(top, detail);
    }
  }

  return { workflows, recent };
}

export default function useActiveDispatches(options = {}) {
  const {
    sessionId = null,
    withDetail = true,
    includeRecent = true,
    pollMs = DEFAULT_POLL_MS,
    fetcher = defaultFetcher,
    enabled = true,
  } = options;

  const [state, setState] = useState({ workflows: [], recent: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const timerRef = useRef(null);
  const firstLoadRef = useRef(true);

  // Manual one-shot refresh (exported). Intentionally does NOT toggle the poll
  // `loading` — that flag means "no data yet", not "a background poll is in flight".
  const refresh = useCallback(async () => {
    try {
      const out = await loadActiveDispatches(fetcher, { sessionId, withDetail, includeRecent });
      if (mountedRef.current) {
        setState(out);
        setError(null);
      }
      return out;
    } catch (e) {
      if (mountedRef.current) setError(e && e.message ? e.message : String(e));
      return { workflows: [], recent: null };
    }
  }, [fetcher, sessionId, withDetail, includeRecent]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Poll loop keyed on the PRIMITIVE inputs (not the `refresh` callback identity),
  // so a new callback ref never spawns a second in-flight chain. The per-run
  // `cancelled` flag is the generation guard: a stale tick that resolves after the
  // deps changed bails without applying its result. `loading` reflects first-load
  // only, so a 2.5s poll does not re-render the whole agents subtree every tick.
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    async function tick() {
      let out = null;
      try {
        out = await loadActiveDispatches(fetcher, { sessionId, withDetail, includeRecent });
      } catch (e) {
        if (!cancelled && mountedRef.current) setError(e && e.message ? e.message : String(e));
      }
      if (cancelled || !mountedRef.current) return;
      if (out) {
        setState(out);
        setError(null);
      }
      if (firstLoadRef.current) {
        firstLoadRef.current = false;
        setLoading(false);
      }
      timerRef.current = setTimeout(tick, pollMs);
    }
    tick();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, pollMs, fetcher, sessionId, withDetail, includeRecent]);

  return { workflows: state.workflows, recent: state.recent, loading, error, refresh };
}
